import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import FarmersNav from "@/components/FarmersNav";
import FarmersIntroCard from "@/components/FarmersIntroCard";
import {
  Tractor, Plant, Ruler, Cow, CalendarCheck, Sparkle, CloudSun,
  Warning, CurrencyDollar, Basket, Notebook, ArrowRight, Plus,
  Trash, MapPin, House, ArrowLeft, Storefront, Package, Bell,
} from "@phosphor-icons/react";
import { fpPath } from "@/lib/site";

const HERO_ILLUSTRATION =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/fsxes2g4_277847F0-845C-4C87-8A92-B7B52E909AC8.png";

const TABS = [
  { key: "farm", label: "My Farm", Icon: Tractor },
  { key: "crops", label: "My Crops", Icon: Plant },
  { key: "fields", label: "My Fields", Icon: Ruler },
  { key: "animals", label: "My Animals", Icon: Cow },
  { key: "inventory", label: "My Inventory", Icon: Package },
  { key: "calendar", label: "My Calendar", Icon: CalendarCheck },
  { key: "observations", label: "My Observations", Icon: Sparkle },
  { key: "weather", label: "My Weather", Icon: CloudSun },
  { key: "alerts", label: "My Alerts", Icon: Warning },
  { key: "expenses", label: "My Expenses", Icon: CurrencyDollar },
  { key: "harvests", label: "My Harvests", Icon: Basket },
  { key: "diary", label: "My Farm Diary", Icon: Notebook },
  { key: "website", label: "My Website", Icon: Storefront },
];

export default function FarmersMine() {
  const { user, loading, requireSignIn } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeTab = params.get("tab") || "farm";
  const [farms, setFarms] = useState([]);
  const [busy, setBusy] = useState(true);
  const [selectedFarmId, setSelectedFarmId] = useState(() => {
    try {
      return localStorage.getItem("np:farmers-mine-farm") || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setBusy(false);
      return;
    }
    http.get("/farmers/farms/mine")
      .then(({ data }) => {
        const list = data.farms || [];
        setFarms(list);
        if (!selectedFarmId && list.length) setSelectedFarmId(list[0].id);
      })
      .catch(() => toast.error("Could not load your farms"))
      .finally(() => setBusy(false));
  }, [user, loading]); // eslint-disable-line

  useEffect(() => {
    try {
      if (selectedFarmId) localStorage.setItem("np:farmers-mine-farm", selectedFarmId);
    } catch { /* ignore */ }
  }, [selectedFarmId]);

  const activeFarm = useMemo(
    () => farms.find((f) => f.id === selectedFarmId) || farms[0] || null,
    [farms, selectedFarmId],
  );

  const setTab = (k) => setParams({ tab: k });

  if (loading) {
    return (
      <main className="bg-[color:var(--np-bg,#F5EBDC)] min-h-screen pt-32 md:pt-36 pb-32 px-5">
        <div className="max-w-[1400px] mx-auto animate-pulse h-64 bg-white/60 rounded-2xl" />
      </main>
    );
  }

  if (!user) {
    return (
      <main
        className="bg-[color:var(--np-bg,#F5EBDC)] min-h-screen pt-32 md:pt-36 pb-32"
        data-testid="farmers-mine-signin"
      >
        <FarmersNav />
        <div className="max-w-[820px] mx-auto px-5 md:px-8 mt-10">
          <Link to={fpPath("/")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
            <ArrowLeft size={11} weight="bold" /> Farmers Pulse
          </Link>
          <div className="mt-4 rounded-3xl border border-dashed border-black/20 bg-white/70 p-10 text-center">
            <Tractor size={22} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-3xl md:text-4xl">Sign in to open My Farm.</div>
            <p className="mt-2 text-sm text-[#3A3830] max-w-lg mx-auto">
              My Farm is your personal record of crops, fields, animals, expenses, harvests and a season-by-season diary.
              It stays private unless you choose to share it.
            </p>
            <button
              onClick={() => requireSignIn("open My Farm")}
              data-testid="farmers-mine-signin-cta"
              className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
            >
              Sign in
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      className="bg-[color:var(--np-bg,#F5EBDC)] text-[#151312] min-h-screen pt-32 md:pt-36 pb-32"
      data-testid="farmers-mine-page"
    >
      <FarmersNav />

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 mt-6">
        {/* Hero row — illustration + intro paragraph */}
        <section className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-6 items-start">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
              <Tractor size={12} weight="fill" /> / My Farm
            </div>
            <h1 className="mt-3 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
              Your farm, in one place.
            </h1>
            <p className="mt-4 font-serif-display text-base md:text-lg italic text-[#3A3830] leading-relaxed max-w-2xl">
              My Farm is your personal record of crops, fields, animals, planting dates, rainfall, harvests, expenses, and
              everything you notice season after season. Year by year, it becomes the history of your own farm — a living
              journal that belongs to you.
            </p>
            {farms.length > 0 && (
              <FarmSwitcher farms={farms} selected={selectedFarmId} onChange={setSelectedFarmId} />
            )}
          </div>
          <div className="rounded-3xl border border-black/10 bg-white overflow-hidden">
            <img
              src={HERO_ILLUSTRATION}
              alt="Farmers Pulse illustration — a farmer with a smartphone documenting the farm"
              className="w-full h-full object-cover aspect-[4/3]"
              data-testid="farmers-mine-illustration"
            />
          </div>
        </section>

        {/* Tabs */}
        <nav className="mt-8 flex flex-wrap gap-2" data-testid="farmers-mine-tabs">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`farmers-mine-tab-${key}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition ${
                activeTab === key
                  ? "border-[color:var(--np-brand,#B85F2A)] bg-[#151312] text-[#F5EBDC]"
                  : "border-black/10 bg-white/70 text-[#3A3830] hover:border-[color:var(--np-brand,#B85F2A)]"
              }`}
            >
              <Icon size={12} weight="fill" />
              {label}
            </button>
          ))}
        </nav>

        {/* Tab body */}
        <section className="mt-6" data-testid={`farmers-mine-panel-${activeTab}`}>
          {busy ? (
            <div className="h-40 rounded-2xl bg-white/60 animate-pulse" />
          ) : farms.length === 0 ? (
            // Inventory is user-scoped and works without a farm; everything else
            // requires at least one farm.
            activeTab === "inventory" ? <InventoryPanel farm={null} /> : <EmptyFarms />
          ) : (
            <TabPanel tab={activeTab} farm={activeFarm} />
          )}
        </section>

        {/* Intro article beneath the tabs */}
        <section className="mt-14">
          <FarmersIntroCard />
        </section>
      </div>
    </main>
  );
}


// =====================================================================
//  FARM SWITCHER
// =====================================================================
function FarmSwitcher({ farms, selected, onChange }) {
  return (
    <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="farmers-mine-farm-switcher">
      <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Farm:</span>
      <select
        value={selected || farms[0]?.id}
        onChange={(e) => onChange(e.target.value)}
        data-testid="farmers-mine-farm-switcher-select"
        className="rounded-lg border border-black/15 bg-white px-3 py-1.5 text-sm"
      >
        {farms.map((f) => (
          <option key={f.id} value={f.id}>
            {f.name} {f.village ? `— ${f.village}` : ""}
          </option>
        ))}
      </select>
      <Link
        to={fpPath("/farms/new")}
        data-testid="farmers-mine-farm-add"
        className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#B85F2A)]"
      >
        <Plus size={11} weight="bold" /> Add farm
      </Link>
    </div>
  );
}


// =====================================================================
//  EMPTY STATE
// =====================================================================
function EmptyFarms() {
  return (
    <div
      className="rounded-2xl border border-dashed border-black/20 bg-white/70 p-10 text-center"
      data-testid="farmers-mine-empty"
    >
      <House size={22} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
      <div className="font-serif-display text-2xl md:text-3xl">Add your first farm to begin.</div>
      <p className="mt-2 text-sm text-[#3A3830] max-w-md mx-auto">
        A farm record unlocks your crops, fields, animals, calendar, alerts, expenses, harvests and a daily diary.
      </p>
      <Link
        to={fpPath("/farms/new")}
        data-testid="farmers-mine-add-farm-cta"
        className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
      >
        <Plus size={11} weight="bold" /> Add a farm
      </Link>
    </div>
  );
}


// =====================================================================
//  TAB PANEL — router
// =====================================================================
function TabPanel({ tab, farm }) {
  // Inventory is user-scoped, not farm-scoped — render even without a farm.
  if (tab === "inventory") return <InventoryPanel farm={farm} />;
  if (!farm) return null;
  switch (tab) {
    case "farm": return <MyFarmPanel farm={farm} />;
    case "crops": return <MyCropsPanel farm={farm} />;
    case "fields": return <FieldsPanel farm={farm} />;
    case "animals": return <AnimalsPanel farm={farm} />;
    case "inventory": return <InventoryPanel farm={farm} />;
    case "calendar": return <CalendarPanel farm={farm} />;
    case "observations": return <ObservationsPanel farm={farm} />;
    case "weather": return <WeatherPanel farm={farm} />;
    case "alerts": return <AlertsPanel farm={farm} />;
    case "expenses": return <ExpensesPanel farm={farm} />;
    case "harvests": return <HarvestsPanel farm={farm} />;
    case "diary": return <DiaryPanel farm={farm} />;
    case "website": return <WebsitePanel farm={farm} />;
    default: return null;
  }
}


// =====================================================================
//  11. MY INVENTORY — Universal Farm Inventory quick-access panel
// =====================================================================
function InventoryPanel({ farm }) {
  const [att, setAtt] = useState(null);
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      http.get("/farmers/inventory/attention").then((r) => r.data).catch(() => null),
      http.get("/farmers/inventory/items", { params: { limit: 6 } })
        .then((r) => ({ items: r.data.items || [], total: r.data.total || 0 })).catch(() => ({ items: [], total: 0 })),
    ]).then(([a, res]) => { setAtt(a); setItems(res.items); setTotalItems(res.total); }).finally(() => setLoading(false));
  }, []);
  const attentionCount = att?.totals
    ? (att.totals.expiring + att.totals.expired + att.totals.critical + att.totals.low +
       att.totals.reorder + att.totals.needs_attention + att.totals.missing + att.totals.reminders_due)
    : 0;
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="mine-inventory">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#B85F2A]">
            <Package size={11} weight="fill" className="inline mr-1" /> Universal Farm Inventory
          </div>
          <h3 className="mt-1 font-serif-display text-2xl">Everything you own, in one place.</h3>
          <p className="mt-2 text-sm text-[#3A3830] max-w-lg">
            Track produce, crops, tools, tractors, farm-stay furniture — any item that lives on your farm.
            Get alerts when things expire, need repair, or run low, and convert any item into a listing on your farm site.
          </p>
        </div>
        <Link to={fpPath("/inventory")}
          className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm font-medium"
          data-testid="mine-inventory-open">
          Open inventory <ArrowRight size={12} weight="bold" />
        </Link>
      </div>

      {loading ? (
        <div className="mt-6 text-sm text-[#3A3830]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-black/15 p-6 text-center">
          <Package size={28} weight="duotone" className="mx-auto text-[#B85F2A]" />
          <p className="mt-2 text-sm text-[#3A3830]">
            Your inventory is empty — add anything from a tractor to a tomato harvest.
          </p>
          <Link to={fpPath("/inventory")}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-black/15 px-4 py-2 text-sm"
            data-testid="mine-inventory-start">
            <Plus size={12} weight="bold" /> Add your first item
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="Items" value={totalItems} />
            <div className="rounded-xl bg-[#FBF2E1] border border-black/10 p-3">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">Needs attention</div>
              <div className="mt-1 text-2xl font-serif-display flex items-center gap-2">
                {attentionCount}
                {attentionCount > 0 && <Bell size={16} weight="fill" className="text-[#B85F2A]" />}
              </div>
            </div>
            <Stat label="Expiring" value={att?.totals?.expiring || 0} />
            <Stat label="Low stock" value={(att?.totals?.low || 0) + (att?.totals?.critical || 0)} />
          </div>

          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] mb-2">
              Recent items
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {items.map((it) => (
                <Link key={it.id} to={fpPath("/inventory")}
                  className="flex items-center gap-3 rounded-xl border border-black/10 hover:border-[#B85F2A] p-3">
                  <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden shrink-0">
                    {it.photos?.[0]
                      ? <img src={it.photos[0]} alt="" className="w-full h-full object-cover" />
                      : <Package size={14} weight="duotone" className="text-[#3A3830]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{it.name}</div>
                    <div className="text-xs text-[#3A3830]">
                      {it.quantity} {it.unit} · {it.top_category}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}



// =====================================================================
//  12. MY WEBSITE — quick access to the Farmer Website Builder
// =====================================================================
function WebsitePanel({ farm }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    http.get("/farmers/websites/mine")
      .then(({ data }) => setSites(data.websites || []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false));
  }, []);
  const active = sites.filter((s) => ["draft", "published", "closed"].includes(s.status));
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="mine-website">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#B85F2A]">
            <Storefront size={11} weight="fill" className="inline mr-1" /> Farm Website Builder
          </div>
          <h3 className="mt-1 font-serif-display text-2xl">Your farm on the open web.</h3>
          <p className="mt-2 text-sm text-[#3A3830] max-w-lg">
            Turn on a modular farm site — hero, products, deals, journal, contact — in about 5 minutes. Themes are presentation only; your content stays yours.
          </p>
        </div>
        <div>
          {active.length === 0 ? (
            <div className="flex flex-col gap-2 items-stretch">
              <Link to={fpPath("/websites/import")}
                className="inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 text-sm font-medium"
                data-testid="mine-website-import">
                Clone from URL
              </Link>
              <Link to={fpPath("/websites/new")}
                className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm font-medium"
                data-testid="mine-website-create">
                <Plus size={12} weight="bold" /> Create from scratch
              </Link>
            </div>
          ) : (
            <Link to={fpPath(`/websites/${active[0].id}`)}
              className="inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 text-sm font-medium"
              data-testid="mine-website-open">
              Open editor <ArrowRight size={12} weight="bold" />
            </Link>
          )}
        </div>
      </div>
      {loading ? (
        <div className="mt-6 text-sm text-[#3A3830]">Loading…</div>
      ) : sites.length > 0 ? (
        <div className="mt-6 space-y-2">
          {sites.map((s) => (
            <div key={s.id} className="rounded-xl border border-black/10 p-3 flex items-center justify-between text-sm gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{s.farm_name}</div>
                <div className="text-xs text-[#3A3830]">/f/{s.slug} · {s.theme_id} · {s.status}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link to={fpPath(`/websites/${s.id}`)} className="rounded-full border border-black/15 px-3 py-1 text-xs">Edit</Link>
                {s.status === "published" && (
                  <a href={`/f/${s.slug}`} target="_blank" rel="noreferrer" className="rounded-full bg-[#151312] text-[#F5EBDC] px-3 py-1 text-xs">View live</a>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <ul className="mt-6 space-y-2 text-sm text-[#3A3830]">
          <li>· 12 professionally-designed themes to choose from</li>
          <li>· 6 starter packs (basic, seller, farm stand, market vendor, simple, full farm)</li>
          <li>· 15 modular block types you can add or remove any time</li>
          <li>· Publish at <span className="font-mono">/f/your-farm</span> — share on WhatsApp, socials or your own domain (Phase 5)</li>
        </ul>
      )}
    </div>
  );
}


// =====================================================================
//  1. MY FARM (overview)
// =====================================================================
function MyFarmPanel({ farm }) {
  const locLine = [farm.village, farm.district, farm.state, farm.country].filter(Boolean).join(", ");
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="mine-farm-overview">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
            <House size={11} weight="fill" /> Farm · {farm.privacy || "private"}
          </div>
          <div className="mt-1 font-serif-display text-3xl md:text-4xl leading-tight" data-testid="mine-farm-name">
            {farm.name}
          </div>
          {locLine && (
            <div className="mt-2 text-sm text-[#3A3830] inline-flex items-center gap-1">
              <MapPin size={12} weight="fill" /> {locLine}
            </div>
          )}
        </div>
        <Link
          to={fpPath(`/farms/${farm.id}`)}
          data-testid="mine-farm-open"
          className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#B85F2A)]"
        >
          Open full farm page <ArrowRight size={11} weight="bold" />
        </Link>
      </div>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        {farm.size !== undefined && farm.size !== null && (
          <Stat label="Size" value={`${farm.size} ${farm.size_unit || ""}`} />
        )}
        {farm.irrigation_type && <Stat label="Irrigation" value={farm.irrigation_type} />}
        {farm.rain_fed !== undefined && <Stat label="Water" value={farm.rain_fed ? "Rain-fed" : "Irrigated"} />}
        {farm.organic_status && <Stat label="Status" value={farm.organic_status} />}
      </div>
      {(farm.crops || []).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {farm.crops.map((c) => (
            <Link
              key={c}
              to={fpPath(`/crops/${encodeURIComponent(c.toLowerCase())}`)}
              className="rounded-full bg-[#FBF2E1] border border-black/10 px-2.5 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:border-black/40"
            >
              {c}
            </Link>
          ))}
        </div>
      )}
      {farm.notes && (
        <p className="mt-4 text-sm text-[#3A3830] whitespace-pre-wrap leading-relaxed">{farm.notes}</p>
      )}
    </div>
  );
}


// =====================================================================
//  2. MY CROPS
// =====================================================================
function MyCropsPanel({ farm }) {
  const crops = farm.crops || [];
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="mine-crops">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Crops on this farm</h3>
        <span className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
          {crops.length} crops
        </span>
      </div>
      {crops.length === 0 ? (
        <p className="mt-3 text-sm text-[#3A3830]">
          You haven't listed any crops for this farm yet. Edit the farm to add crops, or explore the{" "}
          <Link to={fpPath("/crops")} className="text-[color:var(--np-brand,#B85F2A)] underline">crop database</Link>.
        </p>
      ) : (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {crops.map((c) => (
            <Link
              key={c}
              to={fpPath(`/crops/${encodeURIComponent(c.toLowerCase())}`)}
              data-testid={`mine-crop-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-xl border border-black/10 bg-[#FBF2E1]/40 p-4 hover:border-[color:var(--np-brand,#B85F2A)]"
            >
              <div className="font-serif-display text-lg leading-tight">{c}</div>
              <div className="mt-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] inline-flex items-center gap-1">
                Open crop pulse <ArrowRight size={10} weight="bold" />
              </div>
            </Link>
          ))}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to={fpPath("/crops")}
          className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#B85F2A)]"
        >
          Browse all crops
        </Link>
        <Link
          to={fpPath(`/farms/${farm.id}`)}
          className="rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
        >
          Edit crops on this farm
        </Link>
      </div>
    </div>
  );
}


// =====================================================================
//  3. MY FIELDS
// =====================================================================
function FieldsPanel({ farm }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FIELD);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    http.get(`/farmers/fields?farm_id=${farm.id}`)
      .then(({ data }) => setFields(data.fields || []))
      .catch(() => setFields([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [farm.id]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please give the field a name.");
    setSaving(true);
    try {
      await http.post("/farmers/fields", {
        farm_id: farm.id,
        name: form.name.trim(),
        area: form.area ? parseFloat(form.area) : undefined,
        area_unit: form.area_unit || undefined,
        crop: form.crop.trim() || undefined,
        soil: form.soil.trim() || undefined,
        irrigation: form.irrigation.trim() || undefined,
        boundary_notes: form.boundary_notes.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      toast.success("Field added");
      setForm(EMPTY_FIELD);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (fid) => {
    if (!window.confirm("Delete this field?")) return;
    try {
      await http.delete(`/farmers/fields/${fid}`);
      toast.success("Field deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-4" data-testid="mine-fields">
      <div className="rounded-2xl border border-black/10 bg-white p-6">
        <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Fields on this farm</h3>
        <p className="mt-1 text-sm text-[#3A3830]">Sub-plots inside your farm. Track the crop, area and notes for each.</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <FormInput label="Field name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="field-name" />
          <FormInput label="Area" value={form.area} onChange={(v) => setForm({ ...form, area: v })} testid="field-area" />
          <FormSelect
            label="Area unit"
            value={form.area_unit}
            onChange={(v) => setForm({ ...form, area_unit: v })}
            options={[["acre", "acre"], ["hectare", "hectare"], ["sqm", "sqm"], ["cent", "cent"]]}
            testid="field-unit"
          />
          <FormInput label="Crop planted" value={form.crop} onChange={(v) => setForm({ ...form, crop: v })} testid="field-crop" />
          <FormInput label="Soil type" value={form.soil} onChange={(v) => setForm({ ...form, soil: v })} testid="field-soil" />
          <FormInput label="Irrigation" value={form.irrigation} onChange={(v) => setForm({ ...form, irrigation: v })} testid="field-irrigation" />
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormTextarea label="Boundary notes" value={form.boundary_notes} onChange={(v) => setForm({ ...form, boundary_notes: v })} testid="field-boundary" />
          <FormTextarea label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="field-notes" />
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={submit}
            disabled={saving}
            data-testid="field-submit"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
          >
            <Plus size={11} weight="bold" /> {saving ? "Saving…" : "Add field"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="h-24 rounded-2xl bg-white/60 animate-pulse" />
      ) : fields.length === 0 ? (
        <EmptyRow label="No fields yet." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="mine-fields-list">
          {fields.map((f) => (
            <div key={f.id} className="rounded-2xl border border-black/10 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    {f.crop || "no crop set"}
                    {f.area ? ` · ${f.area} ${f.area_unit || ""}` : ""}
                  </div>
                  <div className="mt-1 font-serif-display text-lg leading-tight" data-testid={`mine-field-${f.id}`}>{f.name}</div>
                  {f.soil && <div className="text-xs text-[#3A3830]">Soil: {f.soil}</div>}
                  {f.irrigation && <div className="text-xs text-[#3A3830]">Irrigation: {f.irrigation}</div>}
                  {f.notes && <p className="mt-2 text-xs text-[#3A3830] whitespace-pre-wrap">{f.notes}</p>}
                </div>
                <button onClick={() => remove(f.id)} data-testid={`mine-field-delete-${f.id}`} className="p-1.5 rounded-full border border-black/15 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700"><Trash size={11} weight="fill" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
const EMPTY_FIELD = { name: "", area: "", area_unit: "acre", crop: "", soil: "", irrigation: "", boundary_notes: "", notes: "" };


// =====================================================================
//  4. MY ANIMALS
// =====================================================================
function AnimalsPanel({ farm }) {
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kinds, setKinds] = useState([]);
  const [form, setForm] = useState(EMPTY_ANIMAL);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    http.get(`/farmers/animals?farm_id=${farm.id}`)
      .then(({ data }) => setAnimals(data.animals || []))
      .catch(() => setAnimals([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    http.get("/farmers/animals/kinds").then(({ data }) => setKinds(data.kinds || [])).catch(() => {});
  }, [farm.id]);

  const submit = async () => {
    if (!form.kind) return toast.error("Please pick an animal kind.");
    setSaving(true);
    try {
      await http.post("/farmers/animals", {
        farm_id: farm.id,
        kind: form.kind,
        breed: form.breed.trim() || undefined,
        label: form.label.trim() || undefined,
        count: form.count ? parseInt(form.count, 10) : 1,
        purpose: form.purpose.trim() || undefined,
        housing: form.housing.trim() || undefined,
        feed: form.feed.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      toast.success("Livestock added");
      setForm(EMPTY_ANIMAL);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (aid) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await http.delete(`/farmers/animals/${aid}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-4" data-testid="mine-animals">
      <div className="rounded-2xl border border-black/10 bg-white p-6">
        <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Livestock, poultry & aquaculture</h3>
        <p className="mt-1 text-sm text-[#3A3830]">Roster of animals kept on this farm.</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <FormSelect
            label="Kind *"
            value={form.kind}
            onChange={(v) => setForm({ ...form, kind: v })}
            options={[["", "— pick one —"], ...kinds.map((k) => [k, k])]}
            testid="animal-kind"
          />
          <FormInput label="Breed" value={form.breed} onChange={(v) => setForm({ ...form, breed: v })} testid="animal-breed" />
          <FormInput label="Herd / label" value={form.label} onChange={(v) => setForm({ ...form, label: v })} testid="animal-label" />
          <FormInput label="Count" value={form.count} onChange={(v) => setForm({ ...form, count: v })} testid="animal-count" />
          <FormInput label="Purpose" value={form.purpose} onChange={(v) => setForm({ ...form, purpose: v })} testid="animal-purpose" placeholder="milk · meat · eggs" />
          <FormInput label="Housing" value={form.housing} onChange={(v) => setForm({ ...form, housing: v })} testid="animal-housing" />
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormTextarea label="Feed" value={form.feed} onChange={(v) => setForm({ ...form, feed: v })} testid="animal-feed" />
          <FormTextarea label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="animal-notes" />
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={submit}
            disabled={saving}
            data-testid="animal-submit"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
          >
            <Plus size={11} weight="bold" /> {saving ? "Saving…" : "Add livestock"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="h-24 rounded-2xl bg-white/60 animate-pulse" />
      ) : animals.length === 0 ? (
        <EmptyRow label="No animals recorded yet." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="mine-animals-list">
          {animals.map((a) => (
            <div key={a.id} className="rounded-2xl border border-black/10 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] capitalize">
                    {a.kind}{a.breed ? ` · ${a.breed}` : ""}{a.count ? ` · ${a.count}` : ""}
                  </div>
                  <div className="mt-1 font-serif-display text-lg leading-tight" data-testid={`mine-animal-${a.id}`}>{a.label || a.kind}</div>
                  {a.purpose && <div className="text-xs text-[#3A3830]">Purpose: {a.purpose}</div>}
                  {a.housing && <div className="text-xs text-[#3A3830]">Housing: {a.housing}</div>}
                  {a.feed && <div className="text-xs text-[#3A3830]">Feed: {a.feed}</div>}
                  {a.notes && <p className="mt-2 text-xs text-[#3A3830] whitespace-pre-wrap">{a.notes}</p>}
                </div>
                <button onClick={() => remove(a.id)} data-testid={`mine-animal-delete-${a.id}`} className="p-1.5 rounded-full border border-black/15 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700"><Trash size={11} weight="fill" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
const EMPTY_ANIMAL = { kind: "", breed: "", label: "", count: 1, purpose: "", housing: "", feed: "", notes: "" };


// =====================================================================
//  5. MY CALENDAR
// =====================================================================
function CalendarPanel({ farm }) {
  const [cal, setCal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = farm.latitude && farm.longitude
      ? `?latitude=${farm.latitude}&longitude=${farm.longitude}`
      : "";
    http.get(`/farmers/calendar${q}`)
      .then(({ data }) => setCal(data))
      .catch(() => setCal(null))
      .finally(() => setLoading(false));
  }, [farm.id, farm.latitude, farm.longitude]);

  const groups = useMemo(() => {
    if (!cal?.crops) return { sow: [], harvest: [], grow: [], rest: [] };
    return cal.crops.reduce((acc, c) => {
      const k = c.what_to_do_now || "rest";
      (acc[k] || (acc[k] = [])).push(c);
      return acc;
    }, { sow: [], harvest: [], grow: [], rest: [] });
  }, [cal]);

  const farmCrops = new Set((farm.crops || []).map((c) => c.toLowerCase().replace(/\s+/g, "-")));

  return (
    <div className="space-y-4" data-testid="mine-calendar">
      <div className="rounded-2xl border border-black/10 bg-white p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Your farm calendar</h3>
          {cal && (
            <span className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
              {cal.climate_zone} · {cal.hemisphere} hemisphere · month {cal.current_month}
            </span>
          )}
        </div>
        {!farm.latitude && (
          <p className="mt-2 text-sm text-amber-800">
            Add coordinates on this farm to receive a truly location-aware calendar.
          </p>
        )}
        {loading ? (
          <div className="mt-4 h-40 bg-black/5 rounded-2xl animate-pulse" />
        ) : !cal ? (
          <p className="mt-3 text-sm text-[#3A3830]">Could not load calendar.</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <CalCol title="Sow this week" items={groups.sow} testid="mine-cal-sow" farmCrops={farmCrops} />
            <CalCol title="Ready to harvest" items={groups.harvest} testid="mine-cal-harvest" farmCrops={farmCrops} />
            <CalCol title="Growing now" items={groups.grow} testid="mine-cal-grow" farmCrops={farmCrops} />
            <CalCol title="Resting" items={groups.rest} testid="mine-cal-rest" farmCrops={farmCrops} />
          </div>
        )}
      </div>
    </div>
  );
}
function CalCol({ title, items, testid, farmCrops }) {
  return (
    <div className="rounded-xl border border-black/10 bg-[#FBF2E1]/40 p-4" data-testid={testid}>
      <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">{title}</div>
      {(!items || items.length === 0) ? (
        <div className="mt-2 text-xs text-[#3A3830]">Nothing to note this month.</div>
      ) : (
        <ul className="mt-2 space-y-1">
          {items.slice(0, 10).map((c) => (
            <li key={c.slug} className="flex items-center gap-1.5">
              <span className="text-base">{c.emoji || "🌱"}</span>
              <Link to={fpPath(`/crops/${c.slug}`)} className="text-sm font-serif-display hover:text-[color:var(--np-brand,#B85F2A)]">{c.common_name}</Link>
              {farmCrops.has(c.slug) && <span className="ml-auto rounded-full bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[9px] font-mono-tech tracking-[0.2em] uppercase">on my farm</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// =====================================================================
//  6. MY OBSERVATIONS
// =====================================================================
function ObservationsPanel({ farm }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Pull farmer reports mine
    http.get("/farmers/observations/mine?limit=50")
      .then(({ data }) => setReports(data.reports || data.observations || []))
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, [farm.id]);

  return (
    <div className="space-y-4" data-testid="mine-observations">
      <div className="rounded-2xl border border-black/10 bg-white p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Your observations</h3>
          <Link
            to={fpPath("/report")}
            data-testid="mine-obs-add"
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Plus size={11} weight="bold" /> Report what you see
          </Link>
        </div>
        {loading ? (
          <div className="mt-4 h-32 rounded-2xl bg-black/5 animate-pulse" />
        ) : reports.length === 0 ? (
          <EmptyRow label="You haven't submitted any observations yet." />
        ) : (
          <div className="mt-4 space-y-2">
            {reports.slice(0, 12).map((r) => (
              <div key={r.id} className="rounded-xl border border-black/10 bg-white p-3">
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  {r.category || "observation"}{r.severity ? ` · ${r.severity}` : ""}
                </div>
                <div className="mt-1 font-serif-display text-base leading-tight">{r.title || "Untitled report"}</div>
                {r.description && <div className="mt-1 text-xs text-[#3A3830] line-clamp-2">{r.description}</div>}
                <div className="mt-1 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
                  {new Date(r.created_at).toLocaleDateString()}{r.location_label ? ` · ${r.location_label}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// =====================================================================
//  7. MY WEATHER
// =====================================================================
function WeatherPanel({ farm }) {
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!farm.latitude || !farm.longitude) { setLoading(false); return; }
    setLoading(true);
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${farm.latitude}&longitude=${farm.longitude}` +
      "&current=temperature_2m,relative_humidity_2m,precipitation,wind_speed_10m,weather_code,is_day" +
      "&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,sunrise,sunset" +
      "&timezone=auto&forecast_days=7";
    fetch(url).then((r) => r.json()).then(setW).catch(() => setW(null)).finally(() => setLoading(false));
  }, [farm.id, farm.latitude, farm.longitude]);

  if (!farm.latitude) {
    return (
      <div className="rounded-2xl border border-dashed border-black/20 bg-white/70 p-8 text-center" data-testid="mine-weather-nolatlon">
        <CloudSun size={22} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
        <div className="font-serif-display text-2xl">Add coordinates to your farm to see local weather.</div>
        <Link to={fpPath(`/farms/${farm.id}`)} className="mt-3 inline-block font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline">Edit farm →</Link>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="mine-weather">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Weather on your farm</h3>
        <Link
          to={fpPath("/weather")}
          className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
        >
          Full almanac →
        </Link>
      </div>
      {loading ? (
        <div className="mt-4 h-32 rounded-2xl bg-black/5 animate-pulse" />
      ) : !w?.current ? (
        <p className="mt-3 text-sm text-[#3A3830]">Weather is temporarily unavailable.</p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Temperature" value={`${Math.round(w.current.temperature_2m)}°C`} />
            <Stat label="Humidity" value={`${w.current.relative_humidity_2m}%`} />
            <Stat label="Wind" value={`${Math.round(w.current.wind_speed_10m)} km/h`} />
            <Stat label="Precip" value={`${w.current.precipitation ?? 0} mm`} />
          </div>
          {w.daily && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
              {w.daily.time.map((d, i) => (
                <div key={d} className="rounded-lg border border-black/10 p-2 bg-[#FBF2E1]/40 text-center">
                  <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">
                    {new Date(d).toLocaleDateString(undefined, { weekday: "short" })}
                  </div>
                  <div className="mt-1 font-serif-display text-sm leading-tight">
                    {Math.round(w.daily.temperature_2m_min[i])}° / {Math.round(w.daily.temperature_2m_max[i])}°
                  </div>
                  <div className="text-[10px] text-[#3A3830]">{w.daily.precipitation_sum[i] ?? 0} mm</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// =====================================================================
//  8. MY ALERTS
// =====================================================================
function AlertsPanel({ farm }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="mine-alerts">
      <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Alerts for this farm</h3>
      <p className="mt-1 text-sm text-[#3A3830]">
        Frost, drought, heat, storm and pest early-warnings tuned to your farm's location.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to={fpPath(`/alerts?farm_id=${farm.id}`)}
          data-testid="mine-alerts-open"
          className="rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
        >
          Open alert center
        </Link>
        <Link
          to={fpPath("/pest-heatmap")}
          className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#B85F2A)]"
        >
          Pest heatmap
        </Link>
      </div>
    </div>
  );
}


// =====================================================================
//  9. MY EXPENSES
// =====================================================================
function ExpensesPanel({ farm }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(EMPTY_EXPENSE);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      http.get(`/farmers/expenses?farm_id=${farm.id}&limit=100`),
      http.get(`/farmers/expenses/summary?farm_id=${farm.id}`),
    ]).then(([{ data: a }, { data: b }]) => { setRows(a.expenses || []); setSummary(b); })
      .catch(() => { setRows([]); setSummary(null); })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    http.get("/farmers/expenses/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {});
  }, [farm.id]);

  const submit = async () => {
    if (!form.category) return toast.error("Pick a category");
    if (!form.amount) return toast.error("Enter an amount");
    setSaving(true);
    try {
      await http.post("/farmers/expenses", {
        farm_id: farm.id,
        entry_date: form.entry_date || undefined,
        category: form.category,
        amount: parseFloat(form.amount),
        currency: form.currency || "USD",
        payee: form.payee.trim() || undefined,
        crop: form.crop.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      toast.success("Expense added");
      setForm(EMPTY_EXPENSE);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (eid) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await http.delete(`/farmers/expenses/${eid}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-4" data-testid="mine-expenses">
      <div className="rounded-2xl border border-black/10 bg-white p-6">
        <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Farm expenses ledger</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <FormInput label="Date" value={form.entry_date} onChange={(v) => setForm({ ...form, entry_date: v })} testid="expense-date" placeholder="YYYY-MM-DD" />
          <FormSelect
            label="Category *"
            value={form.category}
            onChange={(v) => setForm({ ...form, category: v })}
            options={[["", "— pick —"], ...cats.map((c) => [c, c])]}
            testid="expense-category"
          />
          <FormInput label="Amount *" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testid="expense-amount" />
          <FormInput label="Currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} testid="expense-currency" placeholder="USD · INR · EUR" />
          <FormInput label="Payee" value={form.payee} onChange={(v) => setForm({ ...form, payee: v })} testid="expense-payee" />
          <FormInput label="Crop / project" value={form.crop} onChange={(v) => setForm({ ...form, crop: v })} testid="expense-crop" />
        </div>
        <div className="mt-3">
          <FormTextarea label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="expense-notes" />
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={submit} disabled={saving} data-testid="expense-submit" className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
            <Plus size={11} weight="bold" /> {saving ? "Saving…" : "Add expense"}
          </button>
        </div>
      </div>

      {summary && summary.total_by_currency?.length > 0 && (
        <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="mine-expenses-summary">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">Total spent</div>
          <div className="mt-1 flex flex-wrap gap-3">
            {summary.total_by_currency.map((t) => (
              <div key={t.currency} className="font-serif-display text-2xl">
                {t.total.toLocaleString()} <span className="text-sm text-[#3A3830]">{t.currency}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="h-24 rounded-2xl bg-white/60 animate-pulse" />
      ) : rows.length === 0 ? (
        <EmptyRow label="No expenses recorded yet." />
      ) : (
        <div className="rounded-2xl border border-black/10 bg-white overflow-hidden" data-testid="mine-expenses-list">
          <table className="w-full text-sm">
            <thead className="bg-[#FBF2E1] text-left">
              <tr>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Date</th>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Category</th>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Amount</th>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Payee</th>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-black/5">
                  <td className="p-2">{r.entry_date}</td>
                  <td className="p-2 capitalize">{r.category}</td>
                  <td className="p-2 font-mono-tech">{r.amount} {r.currency || ""}</td>
                  <td className="p-2">{r.payee || "—"}</td>
                  <td className="p-2 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`mine-expense-delete-${r.id}`} className="p-1 rounded-full border border-black/15 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700"><Trash size={11} weight="fill" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
const EMPTY_EXPENSE = { entry_date: "", category: "", amount: "", currency: "USD", payee: "", crop: "", notes: "" };


// =====================================================================
//  10. MY HARVESTS
// =====================================================================
function HarvestsPanel({ farm }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_HARVEST);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      http.get(`/farmers/harvests?farm_id=${farm.id}&limit=100`),
      http.get(`/farmers/harvests/summary?farm_id=${farm.id}`),
    ]).then(([{ data: a }, { data: b }]) => { setRows(a.harvests || []); setSummary(b); })
      .catch(() => { setRows([]); setSummary(null); })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    http.get("/farmers/harvests/units").then(({ data }) => setUnits(data.units || [])).catch(() => {});
  }, [farm.id]);

  const submit = async () => {
    if (!form.crop.trim()) return toast.error("Enter a crop");
    if (!form.quantity) return toast.error("Enter a quantity");
    setSaving(true);
    try {
      await http.post("/farmers/harvests", {
        farm_id: farm.id,
        entry_date: form.entry_date || undefined,
        crop: form.crop.trim(),
        quantity: parseFloat(form.quantity),
        unit: form.unit || "kg",
        quality: form.quality.trim() || undefined,
        sold: form.sold,
        sale_price: form.sale_price ? parseFloat(form.sale_price) : undefined,
        currency: form.currency || "USD",
        buyer: form.buyer.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      toast.success("Harvest logged");
      setForm(EMPTY_HARVEST);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (hid) => {
    if (!window.confirm("Delete this harvest?")) return;
    try {
      await http.delete(`/farmers/harvests/${hid}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-4" data-testid="mine-harvests">
      <div className="rounded-2xl border border-black/10 bg-white p-6">
        <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Harvest log</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <FormInput label="Date" value={form.entry_date} onChange={(v) => setForm({ ...form, entry_date: v })} testid="harvest-date" placeholder="YYYY-MM-DD" />
          <FormInput label="Crop *" value={form.crop} onChange={(v) => setForm({ ...form, crop: v })} testid="harvest-crop" />
          <FormInput label="Quantity *" value={form.quantity} onChange={(v) => setForm({ ...form, quantity: v })} testid="harvest-quantity" />
          <FormSelect
            label="Unit"
            value={form.unit}
            onChange={(v) => setForm({ ...form, unit: v })}
            options={units.length ? units.map((u) => [u, u]) : [["kg", "kg"]]}
            testid="harvest-unit"
          />
          <FormInput label="Quality" value={form.quality} onChange={(v) => setForm({ ...form, quality: v })} testid="harvest-quality" placeholder="premium · A · B" />
          <FormInput label="Buyer" value={form.buyer} onChange={(v) => setForm({ ...form, buyer: v })} testid="harvest-buyer" />
          <FormInput label="Sale price" value={form.sale_price} onChange={(v) => setForm({ ...form, sale_price: v })} testid="harvest-price" />
          <FormInput label="Currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} testid="harvest-currency" />
          <label className="flex items-center gap-2 text-sm mt-6">
            <input type="checkbox" checked={form.sold} onChange={(e) => setForm({ ...form, sold: e.target.checked })} data-testid="harvest-sold" /> Sold
          </label>
        </div>
        <div className="mt-3">
          <FormTextarea label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="harvest-notes" />
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={submit} disabled={saving} data-testid="harvest-submit" className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
            <Plus size={11} weight="bold" /> {saving ? "Saving…" : "Log harvest"}
          </button>
        </div>
      </div>

      {summary && summary.by_crop?.length > 0 && (
        <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="mine-harvests-summary">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">Total by crop</div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
            {summary.by_crop.slice(0, 8).map((s, i) => (
              <div key={i} className="rounded-xl border border-black/10 bg-[#FBF2E1]/40 p-3">
                <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]">{s.crop}</div>
                <div className="mt-1 font-serif-display text-lg">{s.quantity} {s.unit}</div>
                {s.revenue > 0 && <div className="text-xs text-[#3A3830]">Revenue: {s.revenue}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="h-24 rounded-2xl bg-white/60 animate-pulse" />
      ) : rows.length === 0 ? (
        <EmptyRow label="No harvests logged yet." />
      ) : (
        <div className="rounded-2xl border border-black/10 bg-white overflow-hidden" data-testid="mine-harvests-list">
          <table className="w-full text-sm">
            <thead className="bg-[#FBF2E1] text-left">
              <tr>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Date</th>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Crop</th>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Quantity</th>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Sold</th>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Buyer</th>
                <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-black/5">
                  <td className="p-2">{r.entry_date}</td>
                  <td className="p-2 capitalize">{r.crop}</td>
                  <td className="p-2 font-mono-tech">{r.quantity} {r.unit}</td>
                  <td className="p-2">{r.sold ? `${r.sale_price || ""} ${r.currency || ""}` : "—"}</td>
                  <td className="p-2">{r.buyer || "—"}</td>
                  <td className="p-2 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`mine-harvest-delete-${r.id}`} className="p-1 rounded-full border border-black/15 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700"><Trash size={11} weight="fill" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
const EMPTY_HARVEST = { entry_date: "", crop: "", quantity: "", unit: "kg", quality: "", sold: false, sale_price: "", currency: "USD", buyer: "", notes: "" };


// =====================================================================
//  11. MY FARM DIARY
// =====================================================================
function DiaryPanel({ farm }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    http.get(`/farmers/farms/${farm.id}/diary?limit=60`)
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [farm.id]);

  return (
    <div className="space-y-4" data-testid="mine-diary">
      <div className="rounded-2xl border border-black/10 bg-white p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Farm diary</h3>
          <Link
            to={fpPath(`/farms/${farm.id}`)}
            data-testid="mine-diary-open"
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Plus size={11} weight="bold" /> Add entry
          </Link>
        </div>
        {loading ? (
          <div className="mt-4 h-32 bg-black/5 rounded-2xl animate-pulse" />
        ) : entries.length === 0 ? (
          <EmptyRow label="No diary entries yet." />
        ) : (
          <div className="mt-4 space-y-3">
            {entries.slice(0, 15).map((d) => (
              <div key={d.id} className="rounded-xl border border-black/10 bg-white p-3" data-testid={`mine-diary-entry-${d.id}`}>
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] capitalize">
                  {d.entry_type}{d.crop ? ` · ${d.crop}` : ""}
                </div>
                <div className="mt-1 font-serif-display text-base leading-tight">{d.title || `${d.entry_type} note`}</div>
                <div className="text-[11px] text-[#3A3830]">{d.entry_date}</div>
                {d.body && <p className="mt-1 text-xs text-[#3A3830] whitespace-pre-wrap">{d.body}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// =====================================================================
//  SHARED UI HELPERS
// =====================================================================
const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-black/10 bg-[#FBF2E1]/40 p-3">
    <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</div>
    <div className="mt-1 font-serif-display text-base">{value || "—"}</div>
  </div>
);

const EmptyRow = ({ label }) => (
  <div className="mt-3 rounded-xl border border-dashed border-black/20 bg-white/60 p-6 text-center text-sm text-[#3A3830]">
    {label}
  </div>
);

const FormInput = ({ label, value, onChange, testid, placeholder }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      placeholder={placeholder}
      className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
    />
  </label>
);
const FormSelect = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm capitalize"
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);
const FormTextarea = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</span>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      rows={2}
      className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
    />
  </label>
);
